/* eslint-disable max-len */
import { MenuOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { Col, Layout, Row, Table } from 'antd';
import arrayMove from 'array-move';
import _ from 'lodash';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import { useDebounce } from 'src/hooks/useDebounce';
import { useUpdateProject } from 'src/reactQuery/projects/mutations/useUpdateProject';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import { SearchBy } from './enums/searchby.enum';
import { reorderContent } from '../../actions/reorderActions';
import CButton from '../Button';
import { CStandardInput } from '../Input';
import Modal from '../Modal/Modal';
import { AutocompleteSelect } from '../Select';
import ActionButton from '../buttons/ActionButton';
import './index.css';

const useStyles = makeStyles({
  formRoot: {
    '& .MuiInput-formControl': {
      margin: '0px',
      zIndex: '100'
    }
  },
  inputRoot: {
    backgroundColor: '#F0F4FF',
    borderRadius: '10px',
    fontSize: '12px',
    height: '40px',
    paddingLeft: '15px',
    width: '136px'
  }
});

// menu item drag handle item -------------
const DragHandle = sortableHandle(() => <MenuOutlined style={{ color: '#999', cursor: 'grab' }} />);

const SortableItem = sortableElement((props) => <tr {...(props || {})} />);
const CustomSortableContainer = sortableContainer((props) => <tbody {...props} />);
// drag handle items -----------

/**
 * @typedef {Object} TableAction
 * @property {() => void | Promise<void>} onClick
 * @property {string} title
 * @property {string} type
 * @property {boolean} [disabled]
 * @property {string[]} [SelectedRows]
 */

/**
 * NoxTable component
 * @param {Object} props
 * @param {Array} props.columns
 * @param {any} props.data
 * @param {string[][]} [props.filterList]
 * @param {any}    [props.selectedFilter]
 * @param {TableAction[]} [props.actions]
 * @param {boolean} [props.checkbox]
 * @param {boolean} [props.loading]
 * @param {string}  [props.dragAndDropUrl]
 * @param {(value: string, index?: number, other?: any) => void} [props.onFilterChange]
 * @param {Function} [props.onRowClick]
 * @param {boolean} [props.isDragAndDropEnabled]
 * @param {string} [props.dragAndDropDisabledMessage]
 * @param {(change: {item:any, offset:number}) => void} [props.onReorder]
 * @param {boolean} [props.isSearch]
 * @param {import('./enums/searchby.enum').SearchBy | string | string[]} [props.searchBy]
 * @param {boolean} [props.claim]
 * @param {boolean} [props.increasedPagination]
 * @param {number}  [props.totalPages]
 * @param {(currentPage:number,pageSize:number,search:string,customTypeId:string)=>void} [props.onPageChange]
 * @param {any[]} [props.customTypes]
 * @param {React.ComponentType|Function} [props.expandedRowRender]
 * @param {number[]|string[]} [props.selectedRowKeys]
 * @param {(keys:number[]|string[])=>void} [props.setSelectedRowKeys]
 * @param {string|((record:any, index:number)=>string)} [props.rowClassName]
 */
const NoxTable = ({
  columns,
  data,
  filterList = [],
  actions = /** @type {TableAction[]} */ ([]),
  selectedFilter = [],
  onFilterChange = () => {},
  onRowClick = () => {},
  checkbox = true,
  loading,
  selectedRowKeys = undefined,
  setSelectedRowKeys = () => {},
  searchBy = SearchBy.NAME,
  isDragAndDropEnabled = false,
  isSearch = false,
  dragAndDropUrl = '',
  dragAndDropDisabledMessage,
  claim = false,
  increasedPagination = false,
  totalPages,
  onPageChange = undefined,
  onReorder,
  customTypes = [],
  expandedRowRender,
  rowClassName = undefined
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const history = useHistory();
  const urlLocation = useLocation();
  const dispatch = useDispatch();
  const classes = useStyles();
  useDebounce(() => onSearch(searchTerm), 500, [searchTerm]);
  const { data: project, refetch } = useProject();
  const mutation = useUpdateProject({
    onSuccess: () => {
      refetch();
    }
  });

  const getSearchString = useCallback(() => {
    const { search } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    return search || '';
  }, [urlLocation.search]);

  const search = getSearchString();

  const onSearch = (searchTerm) => {
    const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

    params.search = searchTerm;

    history.replace({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });
  };

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    onChange: onSelectChange,
    selectedRowKeys
  };

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  function onChange(pagination, filters, sorter) {
    params.page = pagination.current;
    params.pageSize = pagination.pageSize;
    params.orderBy = sorter.columnKey;
    switch (sorter.order) {
      case 'ascend':
        params.orderDirection = 1;
        break;
      case 'descend':
        params.orderDirection = -1;
        break;
      case undefined:
        delete params.orderDirection;
        delete params.orderBy;
        break;
      default:
    }

    history.push({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });

    if (onPageChange !== undefined) {
      const searchItem = params.search;
      let customTypeId = '';
      let selectedCustomType;

      if (customTypes.length !== 0 && params?.filter && params.filter[0]) {
        if (Array.isArray(params.filter)) {
          const tempSelectedCustomType = customTypes.filter(
            (customType) => customType.name === params.filter[0]
          )[0];
          selectedCustomType = tempSelectedCustomType;
        } else {
          const tempSelectedCustomType = customTypes.filter(
            (customType) => customType.name === params.filter
          )[0];
          selectedCustomType = tempSelectedCustomType;
        }
      }

      if (selectedCustomType) customTypeId = selectedCustomType._id;

      dispatch(
        onPageChange(pagination.current, pagination.pageSize, searchItem || '', customTypeId || '')
      );
    }
  }

  const onSortStartHandler = () => {
    const body = document.querySelector('tbody');
    body.classList.add('dragging-start');
  };

  const filterData = useCallback(
    (data) => {
      if (isSearch) {
        return data;
      }
      if (data) {
        if (Array.isArray(searchBy)) {
          return data.filter((dataItem) => {
            return searchBy.some((field) => {
              return _.get(dataItem, field)
                ?.toLowerCase()
                ?.includes(getSearchString()?.toLowerCase());
            });
          });
        }

        if (data.length && !data[0][searchBy]) {
          return data;
        }

        return data.filter((item) =>
          item[searchBy]?.toLowerCase()?.includes(getSearchString()?.toLowerCase())
        );
      }
    },
    [searchBy, getSearchString]
  );

  const onCancel = () => {
    setShowModal(false);
  };

  // drag n drop functions
  const dataColumns = [
    {
      className: 'drag-visible',
      dataIndex: 'sort',
      render: () => <DragHandle />,
      title: 'Sort',
      width: 30
    },
    ...columns
  ];

  const [dataSource, setDataSource] = useState(filterData(data));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setDataSource(filterData(data));
  }, [data, filterData]);

  const markProjectContentAsModified = () => {
    if (project?.metadata?.deployment) {
      const date = new Date().getTime();
      mutation.mutate({
        ...project,
        metadata: {
          ...project.metadata,
          deployment: {
            ...project.metadata.deployment,
            lastContentModifiedAt: date
          }
        }
      });
    }
  };

  const removeDraggingClass = () => {
    const body = document.querySelector('tbody');
    body.classList.remove('dragging-start');
  };

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      removeDraggingClass();
      return;
    }

    const previousData = dataSource;
    const reorderedData = arrayMove(previousData, oldIndex, newIndex);

    removeDraggingClass();

    if (!onReorder) {
      dispatch(reorderContent(reorderedData, dragAndDropUrl));
      setDataSource(reorderedData);
      markProjectContentAsModified();
      return;
    }

    const movedItem = previousData[oldIndex];
    const offset = newIndex - oldIndex;

    try {
      await onReorder({ item: movedItem, offset });
    } catch (error) {
      return;
    }

    setDataSource(reorderedData);
    markProjectContentAsModified();
  };

  const DraggableContainer = (props) => (
    <CustomSortableContainer
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      onSortStart={onSortStartHandler}
      useDragHandle
      {...props}
    />
  );

  // const renderAdditionalDiv = (record) => {
  //   if (record.key === clickedRow) {
  //     return <div style={{ border: '2px solid red', margin: '8px' }}>Additional Content</div>;
  //   }
  //   return null;
  // };

  // const expandedRowRender = (record) => (
  //   <div>
  //     {renderAdditionalDiv(record)}
  //     {/* Render other content for expanded row if needed */}
  //   </div>
  // );

  const DraggableBodyRow = ({ ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource && dataSource.findIndex((x) => x.key === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const click = () => {
    const actionToFunc = actions[0].onClick;
    actionToFunc();
  };

  const confirmFalse = () => {
    setConfirmDelete(false);
  };

  useEffect(() => {
    if (search !== searchTerm) {
      setSearchTerm(search);
    }
  }, [search]);

  return (
    <Layout
      style={{
        backgroundColor: 'white'
      }}
    >
      <Row align="middle" justify="start" style={{ padding: '0px 40px 0px 35px' }}>
        <Col
          span={16}
          style={{
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          {selectedFilter ? (
            <>
              {filterList.map((filter, i) => {
                return (
                  <div key={i}>
                    <AutocompleteSelect
                      getOptionLabel={(option) => option}
                      id="autocomplete-noxTable"
                      onSelect={(event) => {
                        switch (i) {
                          case 0:
                            return onFilterChange(event, i, selectedFilter[1]);

                          case 1:
                            return onFilterChange(event, i, selectedFilter[0]);

                          case 2:
                            return onFilterChange(event, i, selectedFilter[2]);

                          default:
                            break;
                        }
                      }}
                      options={filter}
                      placeholderValue={selectedFilter[i]}
                      styles={{ marginRight: '16px', width: i === 0 ? '280px' : '200px' }}
                    />
                  </div>
                );
              })}
            </>
          ) : null}

          <div>
            <CStandardInput
              classes={{ root: classes.inputRoot }}
              disableUnderline
              formclasses={{ root: classes.formRoot }}
              htmlFor="standard-input-table"
              id="standard-input-table"
              onChange={(event) => {
                const { value } = event.target;
                setSearchTerm(value);
              }}
              placeholder="Search"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              value={searchTerm}
            />
          </div>
        </Col>

        <Col span={8}>
          <ul
            style={{
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'flex-end',
              listStyle: 'none',
              margin: '0px',
              padding: '0px'
            }}
          >
            {confirmDelete ? (
              <Modal
                footerActions={
                  <>
                    <CButton
                      onClick={() => setConfirmDelete(false)}
                      type="secondary"
                      xstyle={{ height: '40px' }}
                    >
                      Cancel
                    </CButton>
                    <CButton
                      onClick={() => {
                        click();
                        confirmFalse();
                      }}
                      type="primary"
                      xstyle={{ height: '40px' }}
                    >
                      Delete
                    </CButton>
                  </>
                }
                handleClose={() => {
                  onCancel();
                  setConfirmDelete(false);
                }}
                isModalOpen={showModal}
                styles={{
                  '& .MuiDialogContent-root': {
                    paddingTop: '0px'
                  },
                  width: '400px'
                }}
                titleContent={<h2 className="modal-title">Delete Content</h2>}
              >
                <h3>
                  are you sure you want to delete selected field(s)
                  {actions[0].SelectedRows ? ' ' : ''}
                  <strong>{actions[0].SelectedRows?.join(', ')}</strong>? You can’t undo this action
                </h3>
              </Modal>
            ) : (
              actions.map(({ title, type, onClick, disabled }, i) => {
                if (title === 'Delete') {
                  return (
                    <li key={i}>
                      <ActionButton
                        disabled={disabled || false}
                        onClick={() => {
                          setShowModal(true);
                          setConfirmDelete(true);
                        }}
                        version={type}
                      >
                        {title}
                      </ActionButton>
                    </li>
                  );
                }
                return (
                  <li key={i}>
                    <ActionButton disabled={disabled || false} onClick={onClick} version={type}>
                      {title}
                    </ActionButton>
                  </li>
                );
              })
            )}
          </ul>
        </Col>
      </Row>

      {urlLocation.pathname === '/content' ? (
        <div
          style={{
            fontFamily: 'Poppins',
            marginTop: '45px',
            paddingLeft: '40px',
            textAlign: 'left',
            width: '100%'
          }}
        >
          {isDragAndDropEnabled ? (
            <h1>
              Content Drag & Drop is <strong>enabled</strong>.
            </h1>
          ) : (
            <h1>
              Content Drag & Drop is <strong>disabled</strong>.{' '}
              {dragAndDropDisabledMessage || 'Filter content by content model to enable it.'}
            </h1>
          )}
        </div>
      ) : null}

      <Table
        columns={isDragAndDropEnabled ? dataColumns : columns}
        components={{
          body: {
            row: DraggableBodyRow,
            wrapper: DraggableContainer
          }
        }}
        dataSource={dataSource}
        expandable={{
          expandRowByClick: true,
          expandedRowRender
        }}
        loading={loading}
        onChange={onChange}
        onRow={(record, rowIndex) => {
          return {
            onClick: onRowClick(record, rowIndex)
          };
        }}
        pagination={{
          current: Number(params.page) || 1,
          increasedPagination: increasedPagination
            ? { defaultPageSize: 11 }
            : { defaultPageSize: 10 },
          pageSize: Number(params.pageSize) || 10,
          showSizeChanger: true,
          total: totalPages || undefined
        }}
        rowClassName={rowClassName}
        rowSelection={checkbox ? rowSelection : null}
        style={
          !claim
            ? {
                marginTop: '24px',
                maxWidth: '100%'
              }
            : {
                marginTop: '24px',
                maxWidth: '100%',
                overflowX: 'hidden'
              }
        }
      />
    </Layout>
  );
};

export default NoxTable;
